import { useEffect, useState } from "react";

import { platformDashboardApi } from "@/api/admin";
import { Alert } from "@/components/Alert";
import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { SectionCards } from "@/components/section-cards";

import { DataTable } from "./components/DataTable";
import { DashboardSkeleton } from "./components/DashboardSkeleton";

export function PlatformDashboard() {
  const [platformData, setPlatformData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await platformDashboardApi.getMetrics({ days });
        if (!cancelled) setPlatformData(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load platform metrics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (loading && !platformData) {
    return <DashboardSkeleton />;
  }

  const p = platformData?.platform_metrics || {};
  const roster = platformData?.agencies_roster || [];
  const series = platformData?.activity_series || [];

  const cards = [
    {
      title: "Tenant Organizations",
      value: String(p.total_agencies ?? 0),
      badge: `${p.active_agencies || 0} active`,
      trend: "up",
      footer: "Registered agencies on the platform",
      hint: `${p.inactive_agencies || 0} inactive / suspended`,
    },
    {
      title: "System Users",
      value: String(p.total_users ?? 0),
      badge: `${p.superusers_count || 0} super`,
      trend: "up",
      footer: "Accounts across all tenants",
      hint: "Includes agency staff and platform admins",
    },
    {
      title: "Enrolled Clients",
      value: String(p.total_clients ?? 0),
      badge: `${p.active_clients || 0} active`,
      trend: p.active_clients ? "up" : "down",
      footer: "Clients enrolled in care",
      hint: "Across every tenant organization",
    },
    {
      title: "Care Shifts",
      value: String(p.total_visits ?? 0),
      badge: `${p.completed_visits || 0} done`,
      trend: p.completed_visits ? "up" : "down",
      footer: "Visits logged platform-wide",
      hint: "Scheduled and completed care shifts",
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Platform Dashboard"
        description="Cross-tenant metrics, activity, and organization roster."
      />

      {error ? (
        <PageBody>
          <Alert type="error" title="Could not load dashboard">
            {error}
          </Alert>
        </PageBody>
      ) : null}

      <SectionCards cards={cards} />

      <PageBody>
        <ChartAreaInteractive
          series={series}
          days={days}
          onDaysChange={setDays}
          title="Platform Activity"
          description="User logins and care shifts for the selected period"
        />
      </PageBody>

      <DataTable organizations={roster} roles={p.roles_breakdown || {}} />
    </PageShell>
  );
}
